import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Banknote, CreditCard } from 'lucide-react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { useSEO } from '../hooks/useSEO';

export default function Donate() {
  const { t } = useLanguage();
  useSEO({
    title: 'Donate',
    description:
      'Support GRAWEO\'s humanitarian mission. Make a donation to help people rebuild their lives, access essential aid, and recover their livelihoods in Ukraine.',
  });
  const navigate = useNavigate();
  const [selectedPurpose, setSelectedPurpose] = useState('humanitarian');

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-graweo-chocolate to-graweo-olive text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold font-manrope mb-6">
            {t('donate_title')}
          </h1>
          <p className="text-xl text-gray-100 max-w-2xl mx-auto">
            {t('donate_subtitle')}
          </p>
        </div>
      </section>

      {/* Donation Options */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Bank Transfer */}
            <Card className="p-8 text-center">
              <Banknote className="w-12 h-12 text-graweo-chocolate mx-auto mb-4" />
              <h3 className="text-2xl font-bold font-manrope text-gray-900 mb-2">
                {t('bank_title')}
              </h3>
              <p className="text-gray-600 mb-6">
                {t('card_coming_soon')}
              </p>
              <Button variant="primary" disabled className="w-full">
                {t('requisites_title')}
              </Button>
            </Card>

            {/* Card Payment */}
            <Card className="p-8 text-center">
              <CreditCard className="w-12 h-12 text-graweo-terra mx-auto mb-4" />
              <h3 className="text-2xl font-bold font-manrope text-gray-900 mb-2">
                {t('card_title')}
              </h3>
              <p className="text-gray-600 mb-6">
                {t('card_description')}
              </p>
              <Button variant="primary" className="w-full" onClick={() => navigate('/donate/currency')}>
                {t('card_button')}
              </Button>
            </Card>
          </div>
        </div>
      </section>

      {/* Purpose of Donation */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold font-manrope text-gray-900 mb-8">
            {t('purpose_title')}
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              variant={selectedPurpose === 'humanitarian' ? 'secondary' : 'outline'}
              onClick={() => setSelectedPurpose('humanitarian')}
            >
              {t('purpose_humanitarian')}
            </Button>
            <Button
              variant={selectedPurpose === 'idps' ? 'secondary' : 'outline'}
              onClick={() => setSelectedPurpose('idps')}
            >
              {t('purpose_idps')}
            </Button>
            <Button
              variant={selectedPurpose === 'business' ? 'secondary' : 'outline'}
              onClick={() => setSelectedPurpose('business')}
            >
              {t('purpose_business')}
            </Button>
            <Button
              variant={selectedPurpose === 'other' ? 'secondary' : 'outline'}
              onClick={() => setSelectedPurpose('other')}
            >
              {t('purpose_other')}
            </Button>
          </div>
          <p className="mt-8 text-gray-600">
            {t('donate_subtitle')}
          </p>
        </div>
      </section>
    </div>
  );
}